import React, { useContext, useEffect } from 'react';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { DocumentElement } from './DocumentElement';
import { LoanDocumentDto } from 'client';
import { useDocument } from 'hooks/useDocument';
import { useHistory } from 'react-router-dom';
import { RESPONSE_PATH, RESPONSE_VIEW_ERROR } from 'constants/ResponseType';
import { SnackbarContext } from 'context/SnackbarContext';
import { InfoModal } from 'components/modals/InfoModal';
import { useControlModal } from 'hooks/useControlModal';
import { DATA_LAYER_RESPONSE_KEY } from 'constants/DataLayers';
import { Button } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

type DocumentElementProps = {
	crmId: string;
	setCompleted: Function;
	setShowLoadingModal: Function;
	uploadedDocument: LoanDocumentDto[];
	documentType: string;
	documentAccepted?: string;
};
type InfoModalContent = {
	[key: string]: JSX.Element;
};

const INFO_MODAL_CONTEXT: InfoModalContent = {
	CARPETA_TRIBUTARIA: (
		<li className="body-bold primary-color" style={{ marginBottom: 16 }}>
			Carpeta tributaria
			<div className="body gris-6">
				<p style={{ marginBottom: 9 }}>
					Descárgala desde{' '}
					<a
						style={{ textDecoration: 'none' }}
						className="secondary-color"
						href="https://www.sii.cl/"
					>
						www.sii.cl
					</a>{' '}
					con tu clave única
				</p>
				<p className="body-bold" style={{ marginBottom: 9 }}>
					Utiliza tus datos personales para completar la información solicitada (te enviarás la
					carpeta a ti mismo)
				</p>
				<p style={{ marginBottom: 16 }}>
					Una vez finalizado el trámite haz clic en el botón{' '}
					<span className="body-bold">“Guardar el PDF generado”</span> y adjúntalo en la sección
					correspondiente.{' '}
				</p>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						marginBottom: 16,
					}}
				>
					<Button
						variant="contained"
						color="secondary"
						style={{ color: 'white', whiteSpace: 'nowrap', marginLeft: -16 }}
						onClick={() => {
							document.location.href = 'https://www.sii.cl/servicios_online/1047-1702.html';
						}}
						endIcon={<ArrowForwardIcon />}
					>
						Obtener carpeta tributaria
					</Button>
				</div>

				<p style={{ marginBottom: 12 }}>
					Revisa el tutorial paso a paso en la página de Chileatiende:
				</p>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						marginBottom: 16,
					}}
				>
					<Button
						variant="outlined"
						color="primary"
						style={{ whiteSpace: 'nowrap', marginLeft: -16, width: '270px' }}
						onClick={() => {
							document.location.href =
								'https://www.chileatiende.gob.cl/fichas/3262-carpeta-tributaria-electronica';
						}}
						endIcon={<ArrowForwardIcon />}
					>
						Ver a paso a paso
					</Button>
				</div>
			</div>
		</li>
	),
	COMPROBANTE_DOMICILIO: (
		<li className="body-bold primary-color" style={{ marginBottom: 16 }}>
			Comprobante de domicilio
			<p className="body gris-6">
				Puede ser una foto de una cuenta de servicios básicos o casa comercial que esté a tu nombre
				y aparezca tu dirección, no sirven cuentas de autopistas o telefonía móvil.
			</p>
		</li>
	),
	COTIZACIONES_AFP: (
		<li className="body-bold primary-color">
			Certificado últimas 12 cotizaciones AFP
			<p className="body gris-6">
				Puedes solicitarlo directamente en tu AFP o ingresando a la respectiva sucursal virtual en
				la sección de certificados. Recuerda que deben traer el RUT de tu empleador.
			</p>
		</li>
	),
	LIQUIDACION_SUELDO: (
		<li className="body-bold primary-color" style={{ marginBottom: 16 }}>
			Últimas 3 liquidaciones de sueldo
			<p className="body gris-6">Debes solicitarlas a tu actual empleador</p>
		</li>
	),
};

export const UploadDocumentBox = ({
	crmId,
	setCompleted,
	setShowLoadingModal,
	uploadedDocument,
	documentType,
	documentAccepted,
}: DocumentElementProps) => {
	const history = useHistory();
	const { documents, setDocuments, onDelete, addDocument } = useDocument(
		setShowLoadingModal,
		uploadedDocument
	);

	const { open, toggle } = useControlModal(false);

	const { setOpen } = useContext(SnackbarContext);

	useEffect(
		() => {
			setCompleted(documentType, documents.length > 0);
		},
		//eslint-disable-next-line
		[documents.length]
	);

	const getModal = () => {
		let content = INFO_MODAL_CONTEXT[`${documentType}`] || <></>;
		return (
			<InfoModal
				dataLayerKey={DATA_LAYER_RESPONSE_KEY.LOAN_AUTOATENCION_AYUDA_DOCUMENTOS}
				open={open}
				title="¿Dónde encuentro esta documentación?"
				content={<ul style={{ marginLeft: 18 }}>{content}</ul>}
				buttonLabel="Cerrar"
				toggleModal={toggle}
			/>
		);
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				backgroundColor: '#EDEDF1',
				borderRadius: '4px',
				paddingTop: 24,
				paddingBottom: 16,
			}}
		>
			<Button
				style={{
					backgroundColor: 'white',
					color: '#999999',
					borderRadius: '8px',
					border: '1px solid #C4C4C4',
					width: '90%',
					padding: '32px',
					marginBottom: '6px',
				}}
				startIcon={
					<AttachFileIcon
						style={{ transform: 'rotate(25.24deg)', fontSize: 30, fontWeight: 'bolder' }}
					/>
				}
				component="label"
			>
				Adjuntar documentos
				<input
					type="file"
					accept={documentAccepted ? documentAccepted : '.jpg,.jpeg,.png,.pdf'}
					hidden
					onChange={(event) => {
						if (event.target?.files?.[0] && event.target?.files?.[0]?.size <= 4194304) {
							addDocument(
								crmId,
								documentType,
								(response: any) => {
									setDocuments([...documents, response.data]);
								},
								(_: any) => {
									history.push(`${RESPONSE_PATH}/${RESPONSE_VIEW_ERROR}/error`);
								},
								event.target?.files?.[0]
							);
						} else {
							setOpen(true);
						}
					}}
				/>
			</Button>
			<p className="small gris-6" style={{ margin: '0 24px' }}>
				Formato permitido: PNG, PDF, JPG, peso máximo de los archivos 5mb.
			</p>

			{documents.map((document, index) => (
				<DocumentElement key={`index-${index}`} file={document} onDelete={onDelete} />
			))}

			<div
				className="body-small secondary-color"
				style={{ alignSelf: 'flex-end', margin: '8px 32px 0 0', cursor: 'pointer' }}
				onClick={() => toggle()}
			>
				¿Dónde encuentro esta documentación?
			</div>
			{open ? getModal() : null}
		</div>
	);
};
